import axios from "../../axiosInstance";

// Delivery runs: a dated trip with a driver, a start location and an ordered
// list of stops (orders). A run records each stop's outcome; the order's own
// fulfilment status is still saved by the status pop-up's request.

// One page this size holds every run the Runs list shows.
const RUNS_PER_PAGE = 100;

const send = async (request) => {
  let response = { data: null, error: null };
  try {
    const res = await request();
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

// `withStops` also returns each stop's delivery address (for distances).
export const fetchRuns = (status = "", { withStops = false } = {}) =>
  send(() =>
    axios.get(
      `runs/get?perPage=${RUNS_PER_PAGE}${status ? `&status=${status}` : ""}${withStops ? "&with_stops=1" : ""}`,
    ),
  );

export const fetchRun = (id) => send(() => axios.get(`runs/get-by-id/${id}`));

// The orders that could join an open run (its route's self delivery orders
// that aren't fulfilled, cancelled or on an open run): { order_ids }.
export const fetchRunCandidates = (id) =>
  send(() => axios.get(`runs/candidates/${id}`));

export const fetchRunDrivers = () =>
  send(() => axios.get(`runs/drivers-dropdown`));

// A driver for runs: a name and a phone, saved to the account without a login.
export const createRunDriver = (driver) =>
  send(() => axios.post(`runs/drivers/create`, driver));

export const createRun = (run) => send(() => axios.post(`runs/create`, run));

export const updateRun = (id, changes) =>
  send(() => axios.post(`runs/edit/${id}`, changes));

export const addRunOrders = (id, orderIds) =>
  send(() => axios.post(`runs/add-orders/${id}`, { order_ids: orderIds }));

export const removeRunOrders = (id, orderIds) =>
  send(() => axios.post(`runs/remove-orders/${id}`, { order_ids: orderIds }));

export const reorderRunStops = (id, orderIds) =>
  send(() => axios.post(`runs/reorder-stops/${id}`, { order_ids: orderIds }));

// `location` (optional): where the phone was, { lat, lng, accuracy,
// distance }, all but lat and lng in metres.
export const recordStopOutcome = (id, orderId, outcome, reason, location) =>
  send(() =>
    axios.post(`runs/stop-outcome/${id}/${orderId}`, {
      outcome,
      reason,
      ...(location ? { location } : {}),
    }),
  );

// Where the run's driver is now ({ lat, lng, accuracy }); the server keeps
// the last one and answers with it.
export const sendDriverLocation = (id, { lat, lng, accuracy }) =>
  send(() =>
    axios.post(`runs/driver-location/${id}`, {
      lat: Number(lat.toFixed(7)),
      lng: Number(lng.toFixed(7)),
      accuracy: Math.round(accuracy ?? 0),
    }),
  );

// A locked run keeps its stops as they are (none added, taken off, moved or
// reordered) and can't be cancelled; stop outcomes are still recorded.
export const lockRun = (id) => send(() => axios.post(`runs/lock/${id}`));

export const unlockRun = (id) => send(() => axios.post(`runs/unlock/${id}`));

// Closes an open run from its orders (fulfilled from the order page): a
// fulfilled order's stop is delivered and one at Attention partly, when the
// order was saved; no customer is emailed. Any other order refuses it unless
// `closeUnfinished`, which records those stops not delivered. The result is
// the run as saved.
export const completeRun = (id, { closeUnfinished = false } = {}) =>
  send(() =>
    axios.post(
      `runs/complete/${id}`,
      closeUnfinished ? { close_unfinished: true } : {},
    ),
  );

// Moves stops without an outcome to an open, unlocked run (`toRunId`, at the
// end of its stops) or to a new run (`run`: name, run_date, driver_id,
// start_warehouse_id). The result is { from, to }: both runs as saved.
export const moveRunOrders = (id, orderIds, { toRunId = null, run } = {}) =>
  send(() =>
    axios.post(`runs/move-orders/${id}`, {
      order_ids: orderIds,
      ...(toRunId != null ? { to_run_id: toRunId } : { run }),
    }),
  );

// A run under way (a stop has its outcome) is cancelled or deleted only with
// `confirmUnderWay`; without it the API refuses with `under_way: true`.
const underWay = (confirmUnderWay) =>
  confirmUnderWay ? { confirm_under_way: true } : {};

export const cancelRun = (id, { confirmUnderWay = false } = {}) =>
  send(() =>
    axios.post(`runs/edit/${id}`, {
      status: "cancelled",
      ...underWay(confirmUnderWay),
    }),
  );

export const deleteRun = (id, { confirmUnderWay = false } = {}) =>
  send(() =>
    axios.delete(
      `runs/delete/${id}`,
      confirmUnderWay ? { data: underWay(true) } : undefined,
    ),
  );
